import { ref } from "vue";
import { auth } from "@/fire";

function downloadFilename(header: string | null): string {
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  const quoted = header?.match(/filename="((?:[^"\\]|\\.)*)"/i)?.[1];
  return quoted?.replace(/\\(.)/g, "$1") ?? "download";
}

export function useDocumentDownload() {
  const downloading = ref(false);
  const downloadError = ref(false);

  const download = async (documentId: string) => {
    if (downloading.value) return;
    downloading.value = true;
    downloadError.value = false;
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Authentication required");
      const response = await fetch(
        `/backend/documents/${encodeURIComponent(documentId)}/download`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" },
      );
      if (!response.ok) throw new Error("Download denied or unavailable");
      const filename = downloadFilename(
        response.headers.get("Content-Disposition"),
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the browser time to begin saving before releasing the blob.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      downloadError.value = true;
    } finally {
      downloading.value = false;
    }
  };

  return { download, downloading, downloadError };
}
